import './EditableCellRenderer.scss';

import { useAurigaContext } from '@context/AurigaContext';
import ClearIcon from '@mui/icons-material/Clear';
import { IconButton, InputAdornment } from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { isNil } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import { EditableCellRendererProps } from 'types/TOC.types';
import { EnhancedTOCEntry } from 'types/TOCContext.types';

// Default pattern that will be used to sanitize input.
const DEFAULT_ALLOWED_INPUT = /^[a-zA-Z0-9()/ _&-]*$/;

/**
 * Sanitizes the input value by trimming extra spaces.
 * @param value
 */
const trimValue = (value: string) => value.replace(/\s+/g, ' ').trim();

const EditableCellRenderer = ({
  value,
  data,
  column,
  api,
  isEditing,
  onEndEdit,
  onCellValueChange,
  allowedInputPattern = DEFAULT_ALLOWED_INPUT,
  minLength = 3,
  maxLength = 50,
}: EditableCellRendererProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [editValue, setEditValue] = useState<string>(value || '');
  const { toastNotification, closeToastNotification } = useAurigaContext();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const field = column.getColId() as keyof EnhancedTOCEntry;

  // Check with the parent component on whether this cell is currently being edited.
  const isCurrentlyEditing =
    !isNil(data) &&
    isEditing?.({
      data,
      value,
      field,
      api,
    });

  // Reset edit value if the `value` prop changes
  useEffect(() => {
    setEditValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isCurrentlyEditing) {
      const isValid =
        !isNil(editValue) &&
        editValue.length >= minLength &&
        editValue.length <= maxLength &&
        allowedInputPattern.test(editValue);

      if (!isValid) {
        setErrorMessage(
          `Input must be between ${minLength} and ${maxLength} characters long and contain only allowed characters.`,
        );
      } else {
        setErrorMessage(null);
      }
    } else {
      setErrorMessage(null);
    }
  }, [editValue, minLength, maxLength, allowedInputPattern, isCurrentlyEditing]);

  /**
   * Handles displaying error notifications to the user.
   * This callback allows us to resurface the same error notification if it has already timed out on the display.
   */
  const handleErrorNotification = useCallback(() => {
    if (!isCurrentlyEditing || isNil(data?.metadata?.internalId)) {
      return;
    }

    const notificationId = `editable-cell-renderer-${data.metadata.internalId}-${column.getColId()}`;
    if (errorMessage) {
      toastNotification({
        notificationId,
        type: 'error',
        message: errorMessage,
        visible: true,
      });
    } else {
      closeToastNotification(notificationId);
    }
  }, [closeToastNotification, column, data?.metadata.internalId, errorMessage, isCurrentlyEditing, toastNotification]);

  // When the `handleErrorNotification()` callback updates, call it.
  useEffect(() => {
    handleErrorNotification();
  }, [handleErrorNotification]);

  /**
   * Handles resetting the cell value to its original value and notifies the parent component.
   */
  const handleResetValue = useCallback(() => {
    setEditValue(value || '');
    if (data) {
      onEndEdit?.({ data, value, field, api });
    }
    // Clear out any previous error
    setErrorMessage(null);
  }, [api, data, field, onEndEdit, value]);

  /**
   * Handles confirming the change and notifying the parent component.
   *
   * Performs final sanitizing (such as trim) and ensures the value is still valid.
   */
  const handleConfirmChange = useCallback(() => {
    const sanitizedValue = trimValue(editValue);
    setEditValue(sanitizedValue);
    const isValid =
      !isNil(sanitizedValue) &&
      sanitizedValue.length >= minLength &&
      sanitizedValue.length <= maxLength &&
      allowedInputPattern.test(sanitizedValue);

    if (!isNil(data) && isValid && onCellValueChange) {
      onCellValueChange({
        data,
        field,
        newValue: sanitizedValue,
      });
      onEndEdit?.({ data, value, field, api });
    }
  }, [allowedInputPattern, api, data, editValue, field, maxLength, minLength, onCellValueChange, onEndEdit, value]);

  /**
   * Handles input change and sanitizes input based on provided regex pattern.
   * @param e
   */
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value: targetValue, selectionStart },
    } = e;

    if (allowedInputPattern.test(targetValue)) {
      setEditValue(targetValue);
    } else {
      // Invalid input. Revert to previous value and reset caret position.
      // Reason: This keeps the input caret position from jumping to the end of the input value.
      const pos = (selectionStart || targetValue.length) - 1;
      if (inputRef.current) {
        inputRef.current.value = editValue;
        inputRef.current.setSelectionRange(pos, pos);
      }
    }
  };

  /**
   * Handle keydown events for the input field.
   * Enter: Save changes
   * Escape: Cancel changes
   * @param e
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (isNil(data)) {
        return;
      }

      if (e.key === 'Enter') {
        if (!isNil(errorMessage)) {
          // Resurface the error notification and stop processing.
          handleErrorNotification();
          return;
        }
        e.preventDefault();
        // Stage changes on Enter
        handleConfirmChange();
      } else if (e.key === 'Escape') {
        handleResetValue();
      }
    },
    [data, errorMessage, handleConfirmChange, handleErrorNotification, handleResetValue],
  );

  /**
   * Handle blur event for the input field.
   * Stages changes when user clicks away or loses focus.
   */
  const handleBlur = useCallback(() => {
    if (isNil(data)) {
      return;
    }
    // Stage changes when user clicks away or loses focus
    if (onCellValueChange && editValue !== value) {
      if (!isNil(errorMessage)) {
        // Resurface the error notification and stop processing.
        handleErrorNotification();
        return;
      }
      handleConfirmChange();
    }
  }, [data, editValue, errorMessage, handleConfirmChange, handleErrorNotification, onCellValueChange, value]);

  if (isNil(data)) {
    return '';
  }

  // If this cell is being edited, render an input field.
  if (isCurrentlyEditing) {
    return (
      <Box className='editable-cell-renderer'>
        <TextField
          inputRef={inputRef}
          value={editValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          variant='outlined'
          fullWidth
          autoFocus={column.getColId() === 'page_name'}
          autoComplete='off'
          error={Boolean(errorMessage)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end' title='Cancel'>
                  <IconButton onClick={handleResetValue} edge='end' color='primary' size='small'>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
            htmlInput: {
              maxLength,
            },
          }}
        />
      </Box>
    );
  }

  // If this cell is not being edited, just render the value.
  return <div className='cell-content'>{value}</div>;
};

export default EditableCellRenderer;
